import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = ({ formType = 'contact', onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    city: '',
    message: '',
    gdprConsent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Map form type to interest and client type
  const getFormMetadata = () => {
    const metadata = {
      'contact': { interest: 'Общо запитване', client_type: 'buyer' },
      'evaluation': { interest: 'Оцени имот безплатно', client_type: 'seller' },
      'property': { interest: 'Търся имот', client_type: 'buyer' },
      'investor': { interest: 'Инвеститорско запитване', client_type: 'investor' },
      'parcel': { interest: 'Търся парцел', client_type: 'investor' },
      'callback': { interest: 'Обади се', client_type: 'buyer' }
    };
    return metadata[formType] || metadata['contact'];
  };

  const serviceOptions = [
    'Покупка на имот',
    'Продажба на имот',
    'Наемане на имот',
    'Консултация',
    'Инвестиционен анализ',
    'Правни услуги',
    'Финансиране',
    'Строителство',
    'Оценка на имот',
    'Друго'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.gdprConsent) {
      toast.error('Моля, приемете политиката за поверителност');
      return;
    }

    setIsSubmitting(true);

    try {
      const metadata = getFormMetadata();
      
      const leadData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        interest: metadata.interest,
        client_type: metadata.client_type,
        source: 'website',
        message: formData.message || null,
        status: 'new',
        priority: 'medium'
      };

      const response = await fetch(`${BACKEND_URL}/api/leads/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(leadData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }
      
      toast.success('Вашето запитване е изпратено успешно! Ще се свържем с вас скоро.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        serviceType: '',
        city: '',
        message: '',
        gdprConsent: false
      });

      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Възникна грешка. Моля, опитайте отново.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFormTitle = () => {
    const titles = {
      'evaluation': 'Безплатна оценка на имот',
      'property': 'Търся имот',
      'investor': 'Инвеститорско запитване',
      'parcel': 'Търся парцел',
      'callback': 'Обади се',
      'contact': 'Свържете се с нас'
    };
    return titles[formType] || 'Свържете се с нас';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-[#F6F2E8]">
            Име и фамилия *
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
            className="bg-[#111111] border-[#D4AF37]/20 text-[#F6F2E8] focus:border-[#D4AF37]"
            placeholder="Вашето име"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-[#F6F2E8]">
            Телефон *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handleChange}
            className="bg-[#111111] border-[#D4AF37]/20 text-[#F6F2E8] focus:border-[#D4AF37]"
            placeholder="+359 ..."
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#F6F2E8]">
            Имейл
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            className="bg-[#111111] border-[#D4AF37]/20 text-[#F6F2E8] focus:border-[#D4AF37]"
            placeholder="email@example.com"
          />
        </div>

        {(formType === 'evaluation' || formType === 'property' || formType === 'parcel') && (
          <div className="space-y-2">
            <Label htmlFor="city" className="text-[#F6F2E8]">
              Град / Населено място *
            </Label>
            <Input
              id="city"
              name="city"
              type="text"
              required={formType === 'evaluation'}
              value={formData.city}
              onChange={handleChange}
              className="bg-[#111111] border-[#D4AF37]/20 text-[#F6F2E8] focus:border-[#D4AF37]"
              placeholder="София, Пловдив, Варна..."
            />
          </div>
        )}

        {formType === 'contact' && (
          <div className="space-y-2">
            <Label htmlFor="serviceType" className="text-[#F6F2E8]">
              Тип услуга
            </Label>
            <select
              id="serviceType"
              name="serviceType"
              value={formData.serviceType}
              onChange={handleChange}
              className="w-full h-10 px-3 rounded-md bg-[#111111] border border-[#D4AF37]/20 text-[#F6F2E8] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
            >
              <option value="">Изберете услуга</option>
              {serviceOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-[#F6F2E8]">
          Съобщение
        </Label>
        <Textarea
          id="message"
          name="message"
          rows={5}
          value={formData.message}
          onChange={handleChange}
          className="bg-[#111111] border-[#D4AF37]/20 text-[#F6F2E8] focus:border-[#D4AF37] resize-none"
          placeholder="Допълнителна информация..."
        />
      </div>

      <div className="flex items-start space-x-3">
        <Checkbox
          id="gdprConsent"
          checked={formData.gdprConsent}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, gdprConsent: checked }))}
          className="mt-1 border-[#D4AF37]/40 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
        />
        <Label
          htmlFor="gdprConsent"
          className="text-sm text-[#A9A9A9] leading-relaxed cursor-pointer"
        >
          Съгласен съм с обработката на личните ми данни съгласно{' '}
          <a href="/privacy" className="text-[#D4AF37] hover:underline">
            Политиката за поверителност
          </a>
        </Label>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full md:w-auto btn-primary"
      >
        {isSubmitting ? 'Изпращане...' : 'Изпрати запитване'}
      </Button>
    </form>
  );
};

export default ContactForm;
